import { useEffect, useRef } from "react";
import { fromScreen, project, unproject } from "@oshimap/map-core";
import type { LatLng } from "@oshimap/domain";

export const MIN_ZOOM = 3;
export const MAX_ZOOM = 19;
/** 이 정도는 움직여야 "끌었다" 로 본다. 손가락은 탭할 때도 몇 픽셀 흔들린다. */
const DRAG_SLOP = 4;

export interface Camera { center: LatLng; zoom: number }

export function clampZoom(z: number): number {
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
}

/** 화면의 한 점을 붙잡은 채 줌만 바꾼다 — 커서 아래 지점이 그대로 있어야 한다. */
export function zoomAround(
  cam: Camera,
  nextZoom: number,
  anchor: { x: number; y: number },
  size: { width: number; height: number },
): Camera {
  const z = clampZoom(nextZoom);
  if (z === cam.zoom) return cam;
  const geo = fromScreen(anchor, cam.center, cam.zoom, size);
  const world = project(geo, z);
  const center = unproject(
    { x: world.x - (anchor.x - size.width / 2), y: world.y - (anchor.y - size.height / 2) },
    z,
  );
  return { center, zoom: z };
}

/** 화면을 dx·dy 픽셀만큼 끌었을 때의 새 중심. */
export function panned(cam: Camera, dx: number, dy: number): Camera {
  const world = project(cam.center, cam.zoom);
  return { center: unproject({ x: world.x - dx, y: world.y - dy }, cam.zoom), zoom: cam.zoom };
}

/**
 * 지도를 손으로 움직인다. 드래그·휠·핀치·키보드.
 *
 * 카메라는 **화면이 상태로 들고 있는다.** 어댑터 안에 숨겨 두면
 * 어느 인스턴스를 움직였는지가 불분명해지고, 마커는 따라 움직이는데
 * 조회 범위는 그대로인 상태가 생긴다 (실제로 그랬다).
 *
 * 계산은 전부 Web Mercator 라, Google Basemap SDK를 연결하면 SDK 의 같은 동작으로 바꾸면 된다.
 *
 * 키보드를 함께 넣은 이유: 드래그와 핀치는 키보드로 흉내낼 수 없다.
 * 지도가 마우스로만 움직이면 키보드 사용자에게는 첫 화면이 전부다.
 */
export function useMapGestures(
  ref: React.RefObject<HTMLElement | null>,
  camera: Camera,
  onCamera: (next: Camera) => void,
  /** 실제 SDK 가 제스처를 갖고 있으면 false — 훅은 그대로 부르되 아무것도 붙이지 않는다. */
  enabled = true,
): void {
  const cam = useRef(camera);
  cam.current = camera;
  const emit = useRef(onCamera);
  emit.current = onCamera;

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    const size = () => ({ width: el.clientWidth, height: el.clientHeight });
    const apply = (next: Camera) => { cam.current = next; emit.current(next); };

    const points = new Map<number, { x: number; y: number }>();
    let last: { x: number; y: number } | null = null;
    let pinchDist = 0;
    let dragged = false;

    const distance = () => {
      const [a, b] = [...points.values()];
      return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
    };
    const midpoint = () => {
      const [a, b] = [...points.values()];
      if (!a || !b) return { x: 0, y: 0 };
      const r = el.getBoundingClientRect();
      return { x: (a.x + b.x) / 2 - r.left, y: (a.y + b.y) / 2 - r.top };
    };

    const onDown = (e: PointerEvent) => {
      /* 핀·클러스터 위에서 시작한 눌림은 그 버튼 것이다. */
      if ((e.target as HTMLElement).closest("button")) return;
      points.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (points.size === 1) { last = { x: e.clientX, y: e.clientY }; dragged = false; }
      if (points.size === 2) pinchDist = distance();
      try { el.setPointerCapture(e.pointerId); } catch { /* 합성 이벤트에는 포인터가 없다 */ }
    };

    const onMove = (e: PointerEvent) => {
      if (!points.has(e.pointerId)) return;
      points.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (points.size >= 2) {
        const d = distance();
        if (pinchDist > 0 && Math.abs(d - pinchDist) > 2) {
          apply(zoomAround(cam.current, cam.current.zoom + Math.log2(d / pinchDist), midpoint(), size()));
          pinchDist = d;
        }
        return;
      }

      if (!last) return;
      const dx = e.clientX - last.x;
      const dy = e.clientY - last.y;
      if (!dragged && Math.hypot(dx, dy) < DRAG_SLOP) return;
      dragged = true;
      el.classList.add("om-map--dragging");
      apply(panned(cam.current, dx, dy));
      last = { x: e.clientX, y: e.clientY };
    };

    const onUp = (e: PointerEvent) => {
      points.delete(e.pointerId);
      if (points.size < 2) pinchDist = 0;
      if (points.size === 1) {
        /*
         * 핀치하다 손가락 하나를 떼면 남은 손가락으로 끌기가 이어진다.
         * 이때 `last` 가 첫 손가락 자리에 멈춰 있으면 그 차이만큼 지도가 튄다 —
         * 남은 손가락 위치로 기준을 다시 잡는다.
         */
        const [remaining] = [...points.values()];
        if (remaining) last = { x: remaining.x, y: remaining.y };
      }
      if (points.size === 0) { last = null; el.classList.remove("om-map--dragging"); }
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      /* 트랙패드는 잘게, 휠은 크게 들어온다. 한 번에 한 단계를 넘지 않게 묶는다. */
      const step = Math.max(-1, Math.min(1, -e.deltaY / 240));
      apply(zoomAround(cam.current, cam.current.zoom + step, { x: e.clientX - r.left, y: e.clientY - r.top }, size()));
    };

    const onKey = (e: KeyboardEvent) => {
      const s = size();
      const stepPx = e.shiftKey ? s.width / 2 : 80;
      const c = { x: s.width / 2, y: s.height / 2 };
      switch (e.key) {
        case "ArrowLeft":  apply(panned(cam.current, stepPx, 0)); break;
        case "ArrowRight": apply(panned(cam.current, -stepPx, 0)); break;
        case "ArrowUp":    apply(panned(cam.current, 0, stepPx)); break;
        case "ArrowDown":  apply(panned(cam.current, 0, -stepPx)); break;
        case "+": case "=": apply(zoomAround(cam.current, cam.current.zoom + 1, c, s)); break;
        case "-": case "_": apply(zoomAround(cam.current, cam.current.zoom - 1, c, s)); break;
        default: return;
      }
      e.preventDefault();
    };

    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onUp);
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("keydown", onKey);
    return () => {
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onUp);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("keydown", onKey);
    };
  }, [ref, enabled]);
}
